import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { format, startOfWeek, endOfWeek, subWeeks, addWeeks } from 'date-fns';
import { toET } from '../utils/et';
import openFile from '../utils/openFile';

function formatCurrency(n) { return '$' + Number(n || 0).toFixed(2); }

// Compact address autocomplete (same /estimate/autocomplete AWS GeoPlaces
// source as CreateTripModal). Added 2026-07-16 for the invoice modal.
function InvoiceAddressField({ value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [show, setShow] = useState(false);
  const timerRef = React.useRef(null);
  const handleChange = (val) => {
    onChange(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val.length < 3) { setSuggestions([]); setShow(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/estimate/autocomplete?q=' + encodeURIComponent(val));
        setSuggestions(res.data || []);
        setShow((res.data || []).length > 0);
      } catch (e) { setSuggestions([]); }
    }, 250);
  };
  return (
    <div style={{ position: 'relative' }}>
      <input value={value} onChange={e => handleChange(e.target.value)} placeholder="Start typing the street address..."
        style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
      {show && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white',
          border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 1000, maxHeight: 180, overflowY: 'auto' }}>
          {suggestions.map((s, i) => (
            <div key={i} onClick={() => { onChange(s.label); setSuggestions([]); setShow(false); }}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer' }}
              onMouseEnter={e => e.target.style.background = '#f5f5f3'}
              onMouseLeave={e => e.target.style.background = 'white'}>
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Generate-invoice modal (Paula 2026-07-16: replaces the pipe-format prompts;
// addressee prefills from the patient name, address autocompletes).
// Trip addresses look like "675 Willow Valley Sq, Lancaster, PA 17602-4876,
// United States (The Glen, room # 116)" — split into a mailable address and
// the room/apt tail for the second line.
function splitTripAddress(raw) {
  let addr = String(raw);
  let extra = '';
  const par = addr.match(/\(([^)]*)\)\s*$/);
  if (par) { extra = par[1].trim(); addr = addr.replace(/\s*\([^)]*\)\s*$/, ''); }
  addr = addr.replace(/,?\s*United States\s*$/i, '').replace(/,\s*$/, '').trim();
  return { addr, extra };
}

// Also used in EDIT mode (modal.mode === 'edit') to fix the mailing address on
// an already-generated invoice -- returned Willow Valley mail (Paula 9/8).
function GenerateInvoiceModal({ modal, onClose, onCreate }) {
  const isEdit = modal.mode === 'edit';
  const [invoiceNumber, setInvoiceNumber] = useState(modal.invoiceNumber || '');
  const [addressee, setAddressee] = useState(modal.addressee || '');
  const [address, setAddress] = useState(modal.address || '');
  const [address2, setAddress2] = useState(modal.address2 || '');
  const [busy, setBusy] = useState(false);
  const [sugg, setSugg] = useState(null);
  const [sheetBusy, setSheetBusy] = useState(false);
  // Email vs mail, decided BEFORE the invoice exists (Paula 2026-10-27:
  // Kline's invoices were mailed to his room at the Glen and came back while
  // his wife's email sat in every request).
  const [delivery, setDelivery] = useState(null);
  const [email, setEmail] = useState('');
  // Email or mail is a CHOICE, not a side effect of an email being on file
  // (WV124, Paula 10/27: tapping "Bill this contact" switched to email and
  // wiped the mailing address she meant to use).
  const [sendBy, setSendBy] = useState('mail');
  useEffect(() => {
    api.post('/billing/address-suggestions', { patientName: modal.patientName })
      .then(r => setSugg(r.data))
      .catch(() => setSugg({ suggestions: [], hasFaxSheet: false }));
    if (!isEdit) {
      api.post('/billing/delivery', { patientName: modal.patientName })
        .then(r => { setDelivery(r.data); if (r.data.email) setEmail(r.data.email); setSendBy(r.data.method === 'email' ? 'email' : 'mail'); })
        .catch(() => setDelivery(null));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const fc = delivery && delivery.formContact;
  const useFormContact = () => {
    if (fc.name) setAddressee(fc.name);
    if (fc.email) setEmail(fc.email);
    // Never clear the address on file -- the contact may live there too.
    if (fc.address) { setAddress(fc.address); setAddress2(''); }
    if (fc.email && !fc.address && !address.trim()) setSendBy('email');
  };
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const roomAddress = /\broom\b|675 willow valley|brooke glen|\b\d+(st|nd|rd|th) floor\b/i.test(address + ' ' + address2);
  const canCreate = isEdit ? (!!addressee.trim() && !!address.trim())
    : (!!invoiceNumber.trim() && !!addressee.trim() && (sendBy === 'email' ? emailOk : !!address.trim()));
  const readSheet = async () => {
    setSheetBusy(true);
    try {
      const r = await api.post('/billing/address-from-sheet', { patientName: modal.patientName });
      setAddress(r.data.street + (r.data.cityStateZip ? ', ' + r.data.cityStateZip : ''));
    } catch (err) {
      alert(err.response?.data?.error || 'Could not read the patient sheet');
    } finally { setSheetBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'white', borderRadius: 12, padding: 20, width: 420, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
          {isEdit ? `Edit mailing address — Invoice #${modal.invoiceNumber}` : `Generate Invoice — ${modal.patientName}`}
        </div>
        {isEdit && (
          <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
            Amounts stay as they are. View / Download / Email will use the new address, and it is saved to the customer's profile for the next invoice.
          </div>
        )}
        {!isEdit && fc && (fc.name || '').trim().toLowerCase() !== addressee.trim().toLowerCase() && (
          <div style={{ fontSize: 12, background: '#FFF7E6', border: '0.5px solid #FAC775', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
            <div style={{ color: '#854F0B', fontWeight: 600, marginBottom: 2 }}>The facility gave a billing contact</div>
            <div style={{ color: '#444' }}>{[fc.name, fc.phone, fc.email, fc.address].filter(Boolean).join(' / ')}</div>
            <button onClick={useFormContact} style={{ marginTop: 6, fontSize: 11, padding: '4px 10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
              Bill this contact
            </button>
          </div>
        )}
        {!isEdit && (
          <>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Invoice number</label>
            <input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="e.g. WV44 or PP14"
              style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
          </>
        )}
        {!isEdit && modal.next && (
          <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
            {['WV', 'PP'].map(k => (
              <button key={k} onClick={() => setInvoiceNumber(modal.next[k])} style={{
                fontSize: 11, padding: '3px 10px', background: '#EDF4FB', color: '#185FA5',
                border: '0.5px solid #9DC3E6', borderRadius: 6, cursor: 'pointer',
              }}>Next {k}: {modal.next[k]}</button>
            ))}
          </div>
        )}
        <label style={{ fontSize: 12, color: '#666', display: 'block', margin: '12px 0 4px' }}>Bill to (addressee)</label>
        <input value={addressee} onChange={e => setAddressee(e.target.value)}
          style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
        <label style={{ fontSize: 12, color: '#666', display: 'block', margin: '12px 0 4px' }}>Mailing address</label>
        <InvoiceAddressField value={address} onChange={setAddress} />
        {sugg && (sugg.suggestions.length > 0 || sugg.hasFaxSheet) && (
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: '#999' }}>From their trips:</span>
            {sugg.suggestions.map(s => {
              const parts = splitTripAddress(s.address);
              return (
                <button key={s.address} onClick={() => {
                  setAddress(parts.addr);
                  if (parts.extra && !address2) setAddress2(parts.extra);
                }} title={s.address} style={{
                  fontSize: 11, padding: '4px 10px', background: '#EDF4FB', color: '#185FA5',
                  border: '0.5px solid #9DC3E6', borderRadius: 6, cursor: 'pointer',
                  maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {parts.addr}{s.uses > 1 ? ` (x${s.uses})` : ''}
                </button>
              );
            })}
            {sugg.hasFaxSheet && (
              <button onClick={readSheet} disabled={sheetBusy} style={{
                fontSize: 11, padding: '4px 10px', background: '#EEE9FB', color: '#5B3E96',
                border: '0.5px solid #C3B2E8', borderRadius: 6, cursor: 'pointer', opacity: sheetBusy ? 0.6 : 1,
              }}>
                {sheetBusy ? 'Reading sheet...' : 'Read from patient sheet'}
              </button>
            )}
          </div>
        )}
        <label style={{ fontSize: 12, color: '#666', display: 'block', margin: '12px 0 4px' }}>Room / apt / unit (optional, free-form)</label>
        <input value={address2} onChange={e => setAddress2(e.target.value)} placeholder="e.g. Room 162 NW, Building C"
          style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
        {!isEdit && (
          <>
            <label style={{ fontSize: 12, color: '#666', display: 'block', margin: '12px 0 4px' }}>Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" type="email"
              style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }} />
            <label style={{ fontSize: 12, color: '#666', display: 'block', margin: '12px 0 4px' }}>How should this invoice go out?</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['mail', 'Mail it'], ['email', 'Email it']].map(([k, lbl]) => (
                <button key={k} onClick={() => setSendBy(k)} style={{
                  flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                  background: sendBy === k ? '#185FA5' : '#f5f5f3', color: sendBy === k ? 'white' : '#333',
                  border: sendBy === k ? 'none' : '0.5px solid #ddd',
                }}>{lbl}</button>
              ))}
            </div>
            {(() => {
              const mailTo = [address2, address].filter(Boolean).join(', ');
              const good = sendBy === 'email' ? emailOk : (!!address.trim() && !roomAddress);
              return (
                <div style={{ marginTop: 10, fontSize: 12, borderRadius: 8, padding: '8px 10px',
                  background: good ? (sendBy === 'email' ? '#E1F5EE' : '#EDF4FB') : '#FCEBEB',
                  color: good ? (sendBy === 'email' ? '#0F6E56' : '#185FA5') : '#A32D2D' }}>
                  {sendBy === 'email'
                    ? (emailOk ? <><strong>EMAIL:</strong> emailed to {email.trim()} (CC you). Addressed to {addressee || 'the addressee'}{mailTo ? ', ' + mailTo : ''}.</>
                      : <><strong>EMAIL:</strong> type the email above.</>)
                    : (address.trim() ? <><strong>MAIL:</strong> sent to you to print and mail to {addressee || 'the addressee'}, {mailTo}.
                        {roomAddress && <div style={{ marginTop: 4 }}>This is a room at the Glen. Mail sent there has come back. Bill the family contact if there is one.</div>}</>
                      : <><strong>MAIL:</strong> type the mailing address above.</>)}
                </div>
              );
            })()}
          </>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 14px', fontSize: 13, background: '#f5f5f3', border: '0.5px solid #ddd', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
          <button disabled={busy || !canCreate} onClick={async () => {
            setBusy(true);
            try { await onCreate({ invoiceNumber: invoiceNumber.trim(), addressee: addressee.trim(), address: address.trim(), address2: address2.trim(), email: emailOk ? email.trim() : '', sendBy }); }
            finally { setBusy(false); }
          }} style={{ padding: '8px 14px', fontSize: 13, background: '#185FA5', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: (busy || !canCreate) ? 0.5 : 1 }}>
            {busy ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save address' : 'Create Invoice')}
          </button>
        </div>
      </div>
    </div>
  );
}
function formatTime(ts, fmt) {
  if (!ts) return '--';
  const d = toET(ts);
  if (fmt === 'military') {
    return `${String(d.getHours()).padStart(2,'0')}${String(d.getMinutes()).padStart(2,'0')}`;
  }
  return format(d, 'h:mm a');
}

// Client-side mirror of the backend FACILITY_CONFIG display flags
// (backend/routes/billing.js). Used only when the API config isn't in hand.
const DISPLAY_FLAGS = {
  LGH: { showVehicleType: true, destinationOnly: true },
  LRH: { showVehicleType: true },
};

// Date-cell W prefix + destination cell, matching the emailed PDF
// (backend/Services/invoicePdf.js).
function invoiceRowCells(r, flags) {
  const isWC = r.vehicle_type === 'wheelchair_van' || r.vehicle_type_used === 'wheelchair_van';
  const wPrefix = flags.showVehicleType && isWC ? '<span style="color:#185FA5;font-weight:700">W</span> ' : '';
  let destSource = r.billing_description || r.destination_address || '--';
  if (flags.destinationOnly && destSource.indexOf('\u2192') !== -1) {
    destSource = destSource.split('\u2192').pop().trim();
  }
  return { wPrefix, dest: destSource.substring(0, 40) };
}

// Which patient identifier each facility actually provides.
// LGH issues an MRN. LBHH never does -- they identify patients by date of
// birth (Paula 2026-09-14), so asking for an MRN there is a false alarm.
// Any other payer provides neither, so we do not nag for one at all.
const PATIENT_ID_LABEL = { LGH: 'MRN', LBHH: 'DOB' };
function patientIdLabel(facility) {
  return PATIENT_ID_LABEL[String(facility || '').toUpperCase()] || null;
}

function extractPatientInfo(notes) {
  if (!notes) return { name: null, id: null, idKind: null };
  const parts = notes.split('|').map(p => p.trim());
  const namePart = parts.find(p => p.startsWith('Patient:'));
  const mrnPart = parts.find(p => p.startsWith('MRN:'));
  const dobPart = parts.find(p => p.startsWith('DOB:'));
  return {
    name: namePart ? namePart.replace('Patient:', '').trim() : null,
    id: mrnPart ? mrnPart.replace('MRN:', '').trim() : (dobPart ? dobPart.replace('DOB:', '').trim() : null),
    idKind: mrnPart ? 'MRN' : (dobPart ? 'DOB' : null),
  };
}

export async function printInvoiceById(invoiceId, facilityName, mode, invoiceStatus, onStatusChanged) {
  // mode: 'print' (default) opens the print dialog; 'view' just shows the
  // invoice in the tab so Paula can review it without re-downloading.
  //
  // Printing a DRAFT is how a private pay actually goes out: it gets printed
  // and mailed by hand, never emailed, so nothing ever moved it off draft and
  // every one of them sat in the unsent pile (Paula 2026-09-21: all 31 draft
  // invoices in the system were private pays). Emailing already marks an
  // invoice sent; printing now offers to do the same. Review has its own View
  // button, so reaching for Download means it is going out.
  // Open window immediately (synchronous with click) to avoid popup blocker
  const win = window.open('', '_blank');
  if (!win) { alert('Pop-up blocked — allow popups for this site'); return; }
  win.document.write('<html><head><title>Loading...</title></head><body style="font-family:-apple-system,sans-serif;padding:40px;color:#666"><p>Loading invoice...</p></body></html>');
  try {
    const invData = await api.get(`/billing/invoices/${invoiceId}`);
    const inv = invData.data.invoice;
    const recs = invData.data.records || [];
    const displayFlags = invData.data.config || DISPLAY_FLAGS[facilityName] || {};
    if (recs.length === 0) { win.close(); alert('No records found for this invoice'); return; }

    const invoiceNum = inv.invoice_number;
    const t = { total: 0, miles: 0, hours: 0, tolls: 0, meals: 0 };
    recs.forEach(r => {
      t.total += parseFloat(r.total || 0);
      t.miles += parseFloat(r.mileage_miles || 0);
      t.hours += parseFloat(r.billing_hours || 0);
      t.tolls += parseFloat(r.toll_amount || 0);
      t.meals += parseFloat(r.meal_amount || 0);
    });
    const dates = recs.map(r => new Date(r.scheduled_time)).sort((a,b) => a-b);
    // Prefer the invoice header's stated period (consolidated invoices present
    // as a normal billing week; public_memo explains older lines)
    const periodStart = (inv.period_start && inv.period_end) ? new Date(inv.period_start) : dates[0];
    const periodEnd = (inv.period_start && inv.period_end) ? new Date(inv.period_end) : dates[dates.length - 1];
    const grandTotal = '$' + t.total.toFixed(2);
    // Account credit taken off this invoice (facility_credits, 2026-10-27);
    // same presentation as the server PDF (invoicePdf.js).
    const creditAmount = parseFloat(inv.credit_amount || 0);
    const amountDue = creditAmount > 0 ? '$' + (t.total - creditAmount).toFixed(2) : grandTotal;
    const facilityAddresses = {
      LGH: 'Lancaster General Hospital<br>ATTN Accounts Payable<br>555 North Duke Street<br>Lancaster, PA 17604<br><br><strong>PO #952703</strong>',
      LBHH: 'Lancaster Behavioral Health Hospital<br>ATTN Accounts Payable<br>333 Harrisburg Ave<br>Lancaster, PA 17603',
      LRH: 'Lancaster Rehabilitation Hospital<br>ATTN Accounts Payable<br>675 Good Dr<br>Lancaster, PA 17601',
      'Brooke Glen': 'Brooke Glen Behavioral Hospital<br>ATTN Accounts Payable<br>7170 Lafayette Ave<br>Fort Washington, PA 19034',
      CABHC: 'CABHC<br>2300 Vartan Way, Ste 206<br>Harrisburg, PA 17110',
      'Private Pay': 'Patient',
    };
    const rows = recs.map(r => {
      const tripDate = r.scheduled_time ? format(toET(r.scheduled_time), 'M/d') : '--';
      const startT = r.actual_start_time ? format(toET(r.actual_start_time), 'h:mm a') : '--';
      const returnT = r.actual_return_time ? format(toET(r.actual_return_time), 'h:mm a') : '--';
      const isCancel = parseFloat(r.cancellation_fee || 0) > 0;
      const isReturn = !!r.return_leg_of;
      const cells = invoiceRowCells(r, displayFlags);
      return `<tr style="${isReturn ? 'background:#f9f9f7' : ''}">
        <td>${cells.wPrefix}${tripDate}${isReturn ? ' <span style="color:#185FA5;font-size:9px">RET</span>' : ''}${isCancel ? ' <span style="color:#C62828;font-size:9px">CANCEL</span>' : ''}</td>
        <td>${r.patient_name || r.patient_name_display || '--'}${r.patient_mrn ? '<br>' + r.patient_mrn : ''}</td>
        <td>${cells.dest}</td>
        <td style="text-align:right">${Number(r.mileage_miles || 0).toFixed(0)}</td>
        <td>${startT}</td>
        <td>${returnT}</td>
        <td style="text-align:right">${Number(r.billing_hours || 0).toFixed(2)}</td>
        <td style="text-align:right">${Number(r.toll_amount) > 0 ? '$' + Number(r.toll_amount).toFixed(2) : ''}</td>
        <td style="text-align:right">${Number(r.meal_amount) > 0 ? '$' + Number(r.meal_amount).toFixed(2) : ''}</td>
        <td style="text-align:right;font-weight:500">$${Number(r.total || 0).toFixed(2)}</td>
      </tr>`;
    }).join('');

    // Same vehicle/outcome summary the emailed PDF carries (Anneliese Dyer,
    // LGH, 2026-09-21) so a printed invoice and a mailed one never disagree.
    const vs = { cwc: { n: 0, amt: 0 }, csd: { n: 0, amt: 0 }, xwc: { n: 0, amt: 0 }, xsd: { n: 0, amt: 0 } };
    recs.forEach(function(r) {
      const wc = r.vehicle_type === 'wheelchair_van' || r.vehicle_type_used === 'wheelchair_van';
      const cancelled = parseFloat(r.cancellation_fee || 0) > 0;
      const b = cancelled ? (wc ? 'xwc' : 'xsd') : (wc ? 'cwc' : 'csd');
      vs[b].n += 1; vs[b].amt += parseFloat(r.total || 0);
    });
    const vsRow = (label, cell, red) => cell.n === 0 ? '' :
      `<tr><td style="padding:3px 10px 3px 0;border:none;font-size:11px;${red ? 'color:#C62828;' : ''}">${label}</td>` +
      `<td style="padding:3px 10px;border:none;font-size:11px;text-align:right;font-weight:600">${cell.n}</td>` +
      `<td style="padding:3px 0 3px 10px;border:none;font-size:11px;text-align:right">$${cell.amt.toFixed(2)}</td></tr>`;
    const vehicleSummary = facilityName === 'Private Pay' ? '' :
      `<div style="margin:10px 0 12px;background:#f7f7f5;border-radius:8px;padding:10px 14px;display:inline-block;min-width:320px">
        <div style="font-size:9px;text-transform:uppercase;color:#999;font-weight:600;margin-bottom:5px">Summary by vehicle and outcome</div>
        <table style="width:auto;margin:0;border-collapse:collapse">
          <tr><th style="border:none;padding:0 10px 3px 0;font-size:9px">&nbsp;</th>
              <th style="border:none;padding:0 10px 3px;font-size:9px;text-align:right">Trips</th>
              <th style="border:none;padding:0 0 3px 10px;font-size:9px;text-align:right">Cost</th></tr>
          ${vsRow('Completed &mdash; wheelchair', vs.cwc, false)}
          ${vsRow('Completed &mdash; sedan', vs.csd, false)}
          ${vsRow('Cancelled &mdash; wheelchair', vs.xwc, true)}
          ${vsRow('Cancelled &mdash; sedan', vs.xsd, true)}
        </table>
      </div>`;

    const addrLines = inv.billing_address ? inv.billing_address.split('\n') : [];
    const formattedBillTo = addrLines.length > 0
      ? addrLines.map(function(line) { return '<div>' + line + '</div>'; }).join('')
      : (facilityAddresses[facilityName] || facilityName);

    win.document.open();
    win.document.write(`<html><head><title>Invoice ${invoiceNum}</title>
      <style>
        body { font-family: -apple-system, sans-serif; margin: 40px; font-size: 12px; color: #222; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th { padding: 6px 8px; text-align: left; border-bottom: 2px solid #333; font-size: 10px; text-transform: uppercase; color: #555; }
        td { padding: 5px 8px; border-bottom: 1px solid #eee; font-size: 11px; }
        .total-row td { border-top: 2px solid #333; font-weight: bold; font-size: 12px; }
        /* Paula 2026-10-06: no browser date/URL/page-number header and footer
           on printed invoices, and the address blocks about a centimeter
           higher. A zero @page margin leaves the browser nowhere to print its
           chrome; the body padding supplies the real page margins. */
        @page { size: letter; margin: 0; }
        @media print {
          body { margin: 0; padding: 0.15in 0.5in 0.5in 0.5in; }
          .no-print { display: none !important; }
        }
      </style>
    </head><body>
      <div class="no-print" style="position:fixed;top:10px;right:10px;z-index:10">
        <button onclick="window.print()" style="background:#185FA5;color:white;border:none;border-radius:8px;padding:12px 22px;font-size:15px;font-weight:600;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,0.25)">&#128424; Print</button>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px">
        <div>
          <div style="font-size:22px;font-weight:700;color:#185FA5">Commonwealth Transport</div>
          <div style="font-size:11px;color:#666;margin-top:2px">Non-Emergency Medical Transportation</div>
          <div style="font-size:11px;color:#666;margin-top:4px">554 W King St, Ste 101<br>Lancaster, PA 17603<br>(717) 229-5457</div>
        </div>
        <div style="text-align:right">
          <div style="font-size:20px;font-weight:700;color:#333">INVOICE</div>
          <div style="font-size:22px;font-weight:700;color:#1D9E75;margin-top:4px">${amountDue}</div>
          <div style="font-size:13px;margin-top:4px"><strong>Invoice #</strong> ${invoiceNum}</div>
          <div style="font-size:12px;margin-top:2px"><strong>Date:</strong> ${format(toET(inv.sent_at || inv.created_at || Date.now()), 'M/d/yyyy')}</div>
          <div style="font-size:12px"><strong>Terms:</strong> Net 30</div>
          <div style="font-size:12px"><strong>Due:</strong> ${format(toET(new Date(inv.sent_at || inv.created_at || Date.now()).getTime() + 30*24*60*60*1000), 'M/d/yyyy')}</div>
        </div>
      </div>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px">
        <div style="background:#f7f7f5;border-radius:8px;padding:12px 16px;min-width:240px;margin-left:0.5in">
          <div style="font-size:9px;text-transform:uppercase;color:#999;margin-bottom:4px;font-weight:600">Bill To</div>
          <div style="font-size:12px;line-height:1.5">${formattedBillTo}</div>
        </div>
        <div style="background:#E8F8F0;border-radius:8px;padding:12px 20px;text-align:center;min-width:180px">
          <div style="font-size:9px;text-transform:uppercase;color:#0F6E56;margin-bottom:4px;font-weight:600">Amount Due</div>
          <div style="font-size:26px;font-weight:700;color:#1D9E75">${amountDue}</div>
        </div>
      </div>
      <div style="font-size:13px;font-weight:500;margin-bottom:4px">Invoice Period: ${format(periodStart, 'MMMM d')} – ${format(periodEnd, 'MMMM d, yyyy')}</div>
      ${vehicleSummary}
      ${inv.public_memo ? `<div style="background:#FFFBEB;border:1px solid #F0E1A8;border-radius:8px;padding:10px 14px;font-size:11.5px;color:#6B5B1F;margin-bottom:10px">${inv.public_memo}</div>` : ''}
      <table>
        <thead><tr>
          <th>Date</th><th>Patient</th><th>Destination</th><th style="text-align:right">Miles</th>
          <th>Start</th><th>Return</th><th style="text-align:right">Hours</th>
          <th style="text-align:right">Tolls</th><th style="text-align:right">Meals</th>
          <th style="text-align:right">Total</th>
        </tr></thead>
        <tbody>${rows}
          <tr class="total-row">
            <td colspan="3">Total (${recs.length} trips)</td>
            <td style="text-align:right">${t.miles.toFixed(0)}</td>
            <td></td><td></td>
            <td style="text-align:right">${t.hours.toFixed(2)}</td>
            <td style="text-align:right">${t.tolls > 0 ? '$' + t.tolls.toFixed(2) : ''}</td>
            <td style="text-align:right">${t.meals > 0 ? '$' + t.meals.toFixed(2) : ''}</td>
            <td style="text-align:right">${grandTotal}</td>
          </tr>
          ${creditAmount > 0 ? `<tr><td colspan="9" style="text-align:right;color:#6B5B1F">Less account credit: ${inv.credit_note || ''}</td><td style="text-align:right;color:#6B5B1F">-$${creditAmount.toFixed(2)}</td></tr>
          <tr class="total-row"><td colspan="9" style="text-align:right">Amount due</td><td style="text-align:right">${amountDue}</td></tr>` : ''}
        </tbody>
      </table>
      ${facilityName === 'Private Pay' ? `
      <div style="margin-top:20px;background:#EDF4FB;border:1px solid #9DC3E6;border-radius:8px;padding:14px;font-size:12px;line-height:1.7">
        <div style="font-weight:600;color:#185FA5;font-size:13px;margin-bottom:4px">How to pay</div>
        <strong>By check:</strong> remit to Commonwealth Transport LLC, 554 W King St, Ste 101, Lancaster, PA 17603
        (write invoice <strong>${inv.invoice_number}</strong> on the memo line).<br>
        <strong>Online:</strong> go to <strong>rosie.commonwealthtransport.com/pay</strong>, enter your invoice number
        (<strong>${inv.invoice_number}</strong>) and the patient last name, and pay by card in under a minute.
        Pay by card (3% processing fee) or bank transfer (ACH, no fee).<br>
        <strong>Auto-Pay:</strong> ask us to keep a card or bank account securely on file — each invoice is charged
        automatically the day after it is emailed, after you have had a chance to review it. Call (717) 229-5457 to set it up.
      </div>` : ''}
      <div style="margin-top:24px;font-size:11px;color:#888;text-align:center;border-top:1px solid #eee;padding-top:12px">
        Thank you for your business. Please remit payment to Commonwealth Transport LLC within 30 days.
      </div>
      ${mode !== 'view' ? '<script>window.addEventListener("load",function(){setTimeout(function(){window.print()},400)})</' + 'script>' : ''}
    </body></html>`);
    win.document.close();
    if (mode !== 'view' && invoiceStatus === 'draft') {
      // Give the print dialog a moment, then ask once.
      setTimeout(async () => {
        if (!window.confirm('Invoice printed. Mark it as sent?')) return;
        try {
          await api.put(`/billing/invoices/${invoiceId}`, { status: 'sent' });
          if (onStatusChanged) onStatusChanged();
        } catch (err) {
          alert(err.response?.data?.error || 'Could not mark the invoice sent');
        }
      }, 900);
    }
  } catch (e) {
    console.error('Print invoice error:', e);
    if (win) win.close();
    alert('Failed to load invoice');
  }
}

export default function Billing() {
  const [facility, setFacility] = useState('LGH');
  const [weekOffset, setWeekOffset] = useState(0);
  const [records, setRecords] = useState([]);
  const [totals, setTotals] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [message, setMessage] = useState('');
  const [invModal, setInvModal] = useState(null);
  const [invoiceSearch, setInvoiceSearch] = useState('');

  const createInvoiceFromModal = async ({ invoiceNumber, addressee, address, address2, email, sendBy }) => {
    try {
      // Address block: addressee, optional room/apt line, then street /
      // city-state-zip split at the first comma of the autocompleted address.
      let addrLines = [addressee];
      if (address2) addrLines.push(address2);
      if (address) {
        const ci = address.indexOf(',');
        if (ci > 0) addrLines.push(address.slice(0, ci).trim(), address.slice(ci + 1).trim());
        else addrLines.push(address);
      }
      if (invModal.mode === 'edit') {
        // Address-only fix on an existing invoice (returned mail).
        const r = await api.put(`/billing/invoices/${invModal.invoiceId}/address`, { billingAddress: addrLines.join('\n') });
        setMessage(`Address updated on invoice #${invModal.invoiceNumber}${r.data.profileUpdated ? ' and saved to the customer profile' : ''}. Reprint or re-send it from the list.`);
        setTimeout(() => setMessage(''), 6000);
        setInvModal(null);
        const invRes = await api.get('/billing/invoices?facility=Private Pay');
        setInvoices(invRes.data.invoices || []);
        return;
      }
      const res = await api.post('/billing/generate-patient-invoice', {
        patientName: invModal.patientName, invoiceNumber, billingAddress: addrLines.join('\n'), email: email || undefined, sendBy,
      });
      setMessage(`Invoice #${res.data.invoice.invoice_number} created for ${invModal.patientName} — ${formatCurrency(res.data.invoice.grand_total)}`);
      setTimeout(() => setMessage(''), 5000);
      setInvModal(null);
      const [ppRes, invRes] = await Promise.all([
        api.get('/billing/private-pay-summary'),
        api.get('/billing/invoices?facility=Private Pay'),
      ]);
      setPpPatients(ppRes.data.patients || []);
      setInvoices(invRes.data.invoices || []);
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState(null);
  const [ppPatients, setPpPatients] = useState([]);
  // Private-pay aging (Paula 2026-10-13): outstanding and 30/60/90 past due.
  const [ppAging, setPpAging] = useState(null);
  const [ppAgingOpen, setPpAgingOpen] = useState(null);
  useEffect(() => {
    if (facility !== 'Private Pay') return;
    let alive = true;
    api.get('/billing/private-pay/aging').then(r => { if (alive) setPpAging(r.data); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facility, invoices.length]);
  const [ppExpanded, setPpExpanded] = useState({});
  const [ppRecords, setPpRecords] = useState({});

  // Anchor to ET so week boundaries are correct even if accessed from non-ET timezone
  const nowInET = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const weekStart = startOfWeek(subWeeks(nowInET, weekOffset), { weekStartsOn: 1 });
  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  const startDate = format(weekStart, 'yyyy-MM-dd');
  const endDate = format(weekEnd, 'yyyy-MM-dd');
  const isCurrentWeek = weekOffset === 0;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (facility === 'Private Pay') {
          const [ppRes, invRes] = await Promise.all([
            api.get('/billing/private-pay-summary'),
            api.get(`/billing/invoices?facility=Private Pay`),
          ]);
          setPpPatients(ppRes.data.patients || []);
          setRecords([]);
          setTotals({});
          setInvoices(invRes.data.invoices || []);
          setLoading(false);
          return;
        }
        const [recRes, invRes] = await Promise.all([
          api.get(`/billing/records?facility=${facility}&startDate=${startDate}&endDate=${endDate}`),
          api.get(`/billing/invoices?facility=${facility}`),
        ]);
        setRecords(recRes.data.records || []);
        setTotals(recRes.data.totals || {});
        setNextInvoiceNumber(recRes.data.nextInvoiceNumber || null);
        setInvoices(invRes.data.invoices || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [facility, weekOffset]);

  const handleGenerateInvoice = async () => {
    if (!window.confirm(`Generate invoice for ${facility} (${startDate} to ${endDate})?`)) return;
    setGenerating(true);
    try {
      const res = await api.post('/billing/generate-invoice', { facility, startDate, endDate });
      setMessage(`Invoice #${res.data.invoice.invoice_number} created with ${res.data.recordCount} trips — $${Number(res.data.invoice.grand_total).toFixed(2)}`);
      setTimeout(() => setMessage(''), 5000);
      // Refresh
      const [recRes, invRes] = await Promise.all([
        api.get(`/billing/records?facility=${facility}&startDate=${startDate}&endDate=${endDate}`),
        api.get(`/billing/invoices?facility=${facility}`),
      ]);
      setRecords(recRes.data.records || []);
      setTotals(recRes.data.totals || {});
      setInvoices(invRes.data.invoices || []);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveEdit = async (recordId, patientNameForRefresh) => {
    try {
      await api.put(`/billing/records/${recordId}`, {
        billingHours: editForm.billingHours,
        mileageMiles: editForm.mileageMiles,
        numTransporters: editForm.numTransporters,
        tollAmount: editForm.tollAmount,
        mealAmount: editForm.mealAmount,
        cleanupFee: editForm.cleanupFee,
        patientName: editForm.patientName,
        patientMrn: editForm.patientMrn,
        startTime: editForm.startTime,
        returnTime: editForm.returnTime,
        destinationAddress: editForm.destinationAddress,
      });
      setEditingId(null);
      if (facility === 'Private Pay' && patientNameForRefresh) {
        // Refresh this patient's records and the summary
        const [recRes, ppRes] = await Promise.all([
          api.post('/billing/patient-records', { patientName: patientNameForRefresh }),
          api.get('/billing/private-pay-summary'),
        ]);
        setPpRecords(prev => ({ ...prev, [patientNameForRefresh]: recRes.data.records || [] }));
        setPpPatients(ppRes.data.patients || []);
      } else {
        const res = await api.get(`/billing/records?facility=${facility}&startDate=${startDate}&endDate=${endDate}`);
        setRecords(res.data.records || []);
        setTotals(res.data.totals || {});
      }
    } catch (err) {
      alert('Failed to save');
    }
  };

  const handleApproveWeek = async () => {
    if (!window.confirm(`Approve all ${facility} billing records for ${startDate} to ${endDate}? This unlocks invoice generation.`)) return;
    setApproving(true);
    try {
      await api.post('/billing/approve-week', { facility, startDate, endDate });
      setMessage('Week approved — you can now generate the invoice.');
      setTimeout(() => setMessage(''), 5000);
      const res = await api.get(`/billing/records?facility=${facility}&startDate=${startDate}&endDate=${endDate}`);
      setRecords(res.data.records || []);
      setTotals(res.data.totals || {});
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const handleMarkPaid = async (invoiceId) => {
    try {
      await api.put(`/billing/invoices/${invoiceId}`, { status: 'paid' });
      setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, status: 'paid' } : i));
    } catch (err) {
      alert('Failed to update');
    }
  };

  const unbilledRecords = records.filter(r => !r.invoice_id);
  const billedRecords = records.filter(r => r.invoice_id);

  return (
    <div style={{ padding: '16px 0' }}>
      {invModal && (
        <GenerateInvoiceModal modal={invModal} onClose={() => setInvModal(null)} onCreate={createInvoiceFromModal} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Billing</h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Weekly facility invoices</div>
        </div>
        <button onClick={async () => {
          // If any displayed record has an invoice, fetch ALL records from that invoice
          // (the invoice may span multiple weeks for catch-up billing)
          const invoicedRecord = records.find(r => r.invoice_id);
          let printRecords = records;
          let printTotals = totals;
          let invoiceNum = nextInvoiceNumber || '';
          let printPeriodStart = weekStart;
          let printPeriodEnd = weekEnd;
          let printFlags = DISPLAY_FLAGS[facility] || {};
          if (invoicedRecord) {
            try {
              const invData = await api.get(`/billing/invoices/${invoicedRecord.invoice_id}`);
              printRecords = invData.data.records || records;
              printFlags = invData.data.config || printFlags;
              invoiceNum = invData.data.invoice?.invoice_number || invoiceNum;
              // Compute totals from invoice records
              const t = { total: 0, miles: 0, hours: 0, tolls: 0, meals: 0 };
              printRecords.forEach(r => {
                t.total += parseFloat(r.total || 0);
                t.miles += parseFloat(r.mileage_miles || 0);
                t.hours += parseFloat(r.billing_hours || 0);
                t.tolls += parseFloat(r.toll_amount || 0);
                t.meals += parseFloat(r.meal_amount || 0);
              });
              printTotals = t;
              // Use earliest and latest trip dates as period
              const dates = printRecords.map(r => new Date(r.scheduled_time)).sort((a,b) => a-b);
              if (dates.length > 0) { printPeriodStart = dates[0]; printPeriodEnd = dates[dates.length - 1]; }
            } catch (e) { console.error('Failed to fetch invoice records:', e); }
          }
          const win = window.open('', '_blank');
          if (!win) { alert('Pop-up blocked — please allow pop-ups for this site'); return; }
          const rows = printRecords.map(r => {
            const tripDate = r.scheduled_time ? format(toET(r.scheduled_time), 'M/d') : '--';
            const startT = r.actual_start_time ? format(toET(r.actual_start_time), 'h:mm a') : '--';
            const returnT = r.actual_return_time ? format(toET(r.actual_return_time), 'h:mm a') : '--';
            const isCancel = parseFloat(r.cancellation_fee || 0) > 0;
            const isReturn = !!r.return_leg_of;
            const cells = invoiceRowCells(r, printFlags);
            return `<tr style="${isReturn ? 'background:#f9f9f7' : ''}">
              <td>${cells.wPrefix}${tripDate}${isReturn ? ' <span style="color:#185FA5;font-size:9px">RET</span>' : ''}${isCancel ? ' <span style="color:#C62828;font-size:9px">CANCEL</span>' : ''}</td>
              <td>${r.patient_name || r.patient_name_display || '--'}${r.patient_mrn ? '<br>' + r.patient_mrn : ''}</td>
              <td>${cells.dest}</td>
              <td style="text-align:right">${Number(r.mileage_miles || 0).toFixed(0)}</td>
              <td>${startT}</td>
              <td>${returnT}</td>
              <td style="text-align:right">${Number(r.billing_hours || 0).toFixed(2)}</td>
              <td style="text-align:right">${Number(r.toll_amount) > 0 ? '$' + Number(r.toll_amount).toFixed(2) : ''}</td>
              <td style="text-align:right">${Number(r.meal_amount) > 0 ? '$' + Number(r.meal_amount).toFixed(2) : ''}</td>
              <td style="text-align:right;font-weight:500">$${Number(r.total || 0).toFixed(2)}</td>
            </tr>`;
          }).join('');
          const grandTotal = '$' + Number(printTotals.total || 0).toFixed(2);
          const facilityAddresses = {
            LGH: 'Lancaster General Hospital<br>ATTN Accounts Payable<br>555 North Duke Street<br>Lancaster, PA 17604<br><br><strong>PO #952703</strong>',
            LBHH: 'Lancaster Behavioral Health Hospital<br>ATTN Accounts Payable<br>333 Harrisburg Ave<br>Lancaster, PA 17603',
            LRH: 'Lancaster Rehabilitation Hospital<br>ATTN Accounts Payable<br>675 Good Dr<br>Lancaster, PA 17601',
            'Brooke Glen': 'Brooke Glen Behavioral Hospital<br>ATTN Accounts Payable<br>7170 Lafayette Ave<br>Fort Washington, PA 19034',
            CABHC: 'CABHC<br>2300 Vartan Way, Ste 206<br>Harrisburg, PA 17110',
            'Private Pay': 'Patient',
          };
          win.document.write(`<html><head><title>Invoice ${invoiceNum}</title>
            <style>
              body { font-family: -apple-system, sans-serif; margin: 40px; font-size: 12px; color: #222; }
              table { width: 100%; border-collapse: collapse; margin-top: 12px; }
              th { padding: 6px 8px; text-align: left; border-bottom: 2px solid #333; font-size: 10px; text-transform: uppercase; color: #555; }
              td { padding: 5px 8px; border-bottom: 1px solid #eee; font-size: 11px; }
              .total-row td { border-top: 2px solid #333; font-weight: bold; font-size: 12px; }
              @media print { body { margin: 20px; } }
            </style>
          </head><body>
            <!-- Invoice header -->
            <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px">
              <div>
                <div style="font-size:22px;font-weight:700;color:#185FA5">Commonwealth Transport</div>
                <div style="font-size:11px;color:#666;margin-top:2px">Non-Emergency Medical Transportation</div>
                <div style="font-size:11px;color:#666;margin-top:4px">554 W King St, Ste 101<br>Lancaster, PA 17603<br>(717) 229-5457</div>
              </div>
              <div style="text-align:right">
                <div style="font-size:20px;font-weight:700;color:#333">INVOICE</div>
                <div style="font-size:22px;font-weight:700;color:#1D9E75;margin-top:4px">${grandTotal}</div>
                ${invoiceNum ? '<div style="font-size:13px;margin-top:4px"><strong>Invoice #</strong> ' + invoiceNum + '</div>' : ''}
                <div style="font-size:12px;margin-top:2px"><strong>Date:</strong> ${format(toET(new Date()), 'M/d/yyyy')}</div>
                <div style="font-size:12px"><strong>Terms:</strong> Net 30</div>
                <div style="font-size:12px"><strong>Due:</strong> ${format(toET(Date.now() + 30*24*60*60*1000), 'M/d/yyyy')}</div>
              </div>
            </div>

            <!-- Bill To + Grand Total -->
            <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px">
              <div style="background:#f7f7f5;border-radius:8px;padding:12px 16px;min-width:240px">
                <div style="font-size:9px;text-transform:uppercase;color:#999;margin-bottom:4px;font-weight:600">Bill To</div>
                <div style="font-size:12px;line-height:1.5">${facilityAddresses[facility] || facility}</div>
              </div>
              <div style="background:#E8F8F0;border-radius:8px;padding:12px 20px;text-align:center;min-width:180px">
                <div style="font-size:9px;text-transform:uppercase;color:#0F6E56;margin-bottom:4px;font-weight:600">Amount Due</div>
                <div style="font-size:26px;font-weight:700;color:#1D9E75">${grandTotal}</div>
              </div>
            </div>

            <!-- Invoice period -->
            <div style="font-size:13px;font-weight:500;margin-bottom:4px">Invoice Period: ${format(printPeriodStart, 'MMMM d')} – ${format(printPeriodEnd, 'MMMM d, yyyy')}</div>

            <table>
              <thead><tr>
                <th>Date</th><th>Patient</th><th>Destination</th><th style="text-align:right">Miles</th>
                <th>Start</th><th>Return</th><th style="text-align:right">Hours</th>
                <th style="text-align:right">Tolls</th><th style="text-align:right">Meals</th>
                <th style="text-align:right">Total</th>
              </tr></thead>
              <tbody>${rows}
                <tr class="total-row">
                  <td colspan="3">Total (${printRecords.length} trips)</td>
                  <td style="text-align:right">${Number(printTotals.miles || 0).toFixed(0)}</td>
                  <td></td><td></td>
                  <td style="text-align:right">${Number(printTotals.hours || 0).toFixed(2)}</td>
                  <td style="text-align:right">${Number(printTotals.tolls) > 0 ? '$' + Number(printTotals.tolls).toFixed(2) : ''}</td>
                  <td style="text-align:right">${Number(printTotals.meals) > 0 ? '$' + Number(printTotals.meals).toFixed(2) : ''}</td>
                  <td style="text-align:right">${grandTotal}</td>
                </tr>
              </tbody>
            </table>

            <!-- Footer -->
            <div style="margin-top:24px;font-size:11px;color:#888;text-align:center;border-top:1px solid #eee;padding-top:12px">
              Thank you for your business. Please remit payment to Commonwealth Transport LLC within 30 days.
            </div>
          </body></html>`);
          win.document.close();
          win.print();
        }} style={{ fontSize: 12 }}>Download Invoice</button>
      </div>

      {/* Facility tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {['LGH', 'LBHH', 'LRH', 'CABHC', 'Brooke Glen', 'Private Pay'].map(f => (
          <button key={f} onClick={() => { setFacility(f); setWeekOffset(0); }}
            style={{
              fontSize: 12, padding: '6px 14px', borderRadius: 8,
              background: facility === f ? '#185FA5' : 'white',
              color: facility === f ? 'white' : '#666',
              border: `0.5px solid ${facility === f ? '#185FA5' : '#e0e0db'}`,
              fontWeight: facility === f ? 500 : 400,
            }}>
            {f}
          </button>
        ))}
      </div>

      {/* Week navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={() => setWeekOffset(w => w + 1)} style={{ fontSize: 12, padding: '4px 10px' }}>← Prev</button>
        <div style={{ fontSize: 13, fontWeight: 500 }}>
          {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
        </div>
        <button onClick={() => setWeekOffset(w => Math.max(0, w - 1))} disabled={isCurrentWeek}
          style={{ fontSize: 12, padding: '4px 10px', opacity: isCurrentWeek ? 0.4 : 1 }}>Next →</button>
      </div>

      {message && (
        <div style={{ background: '#E1F5EE', color: '#0F6E56', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {message}
        </div>
      )}

      {/* Summary cards. Private Pay has no weekly billing_records/totals loaded
          (it lists unbilled patients instead), so those cards would read 0.
          Aggregate the private-pay summary into real unbilled totals. */}
      {(() => {
        const cards = facility === 'Private Pay'
          ? [
              { label: 'Unbilled patients', value: ppPatients.length },
              { label: 'Unbilled trips', value: ppPatients.reduce((s, p) => s + (Number(p.trips) || 0), 0) },
              { label: 'Total unbilled', value: formatCurrency(ppPatients.reduce((s, p) => s + (Number(p.total) || 0), 0)), color: '#1D9E75' },
            ]
          : [
              { label: 'Trips', value: records.length },
              { label: 'Total hours', value: Number(totals.hours || 0).toFixed(1) },
              { label: 'Total miles', value: Number(totals.miles || 0).toFixed(0) },
              { label: 'Total billed', value: formatCurrency(totals.total), color: '#1D9E75' },
            ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cards.length}, 1fr)`, gap: 8, marginBottom: 8 }}>
            {cards.map(s => (
              <div key={s.label} style={{ background: '#f5f5f3', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: '#999', marginBottom: 3 }}>{s.label}</div>
                <div style={{ fontSize: 18, fontWeight: 500, color: s.color || '#333' }}>{s.value}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* WC transporter breakdown — admin only, not on invoices.
          Overnights are their own bucket (Paula 2026-08-26): they're always
          billed at 2x by the overnight convention regardless of crew, so
          mixing them into the transporter counts made the numbers read wrong. */}
      {records.length > 0 && (() => {
        const wcRecs = records.filter(r => r.vehicle_type === 'wheelchair_van');
        const wcOvr = wcRecs.filter(r => r.is_overnight).length;
        const wc1 = wcRecs.filter(r => !r.is_overnight && Number(r.num_transporters) === 1).length;
        const wc2 = wcRecs.filter(r => !r.is_overnight && Number(r.num_transporters) >= 2).length;
        const sedan = records.filter(r => r.vehicle_type === 'sedan').length;
        return wcRecs.length > 0 ? (
          <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
            WC: {wcOvr} overnight{wcOvr === 1 ? '' : 's'}, {wc1} x1 transporter, {wc2} x2 transporters | Sedan: {sedan}
          </div>
        ) : null;
      })()}

      {facility === 'Private Pay' && !loading ? (
        <div>
          {ppAging && (
            <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: '12px 16px', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Outstanding private pay</div>
                <div style={{ fontSize: 11, color: '#999' }}>Aged from the day the invoice went out. {ppAging.generatedNotSent?.count ? `${ppAging.generatedNotSent.count} generated but not sent (${formatCurrency(ppAging.generatedNotSent.total)}) not included.` : ''}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginTop: 10 }}>
                {[
                  { key: 'all', label: 'Outstanding', total: ppAging.outstanding.total, count: ppAging.outstanding.count, color: '#185FA5' },
                  { key: 'current', label: 'Current', total: ppAging.buckets.current.total, count: ppAging.buckets.current.count, color: '#0F6E56' },
                  { key: 'd30', label: '30 days', total: ppAging.buckets.d30.total, count: ppAging.buckets.d30.count, color: '#854F0B' },
                  { key: 'd60', label: '60 days', total: ppAging.buckets.d60.total, count: ppAging.buckets.d60.count, color: '#A32D2D' },
                  { key: 'd90', label: '90+ days', total: ppAging.buckets.d90.total, count: ppAging.buckets.d90.count, color: '#7A1F1F' },
                ].map(b => (
                  <div key={b.key} onClick={() => setPpAgingOpen(ppAgingOpen === b.key ? null : b.key)}
                    style={{ background: ppAgingOpen === b.key ? '#EDF4FB' : '#f5f5f3', borderRadius: 8, padding: '10px 12px', cursor: 'pointer', border: `0.5px solid ${ppAgingOpen === b.key ? '#185FA5' : 'transparent'}` }}>
                    <div style={{ fontSize: 10, color: '#999', marginBottom: 3 }}>{b.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 500, color: b.color }}>{formatCurrency(b.total)}</div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>{b.count} invoice{b.count === 1 ? '' : 's'}</div>
                  </div>
                ))}
              </div>
              {ppAgingOpen && (() => {
                const list = ppAgingOpen === 'all'
                  ? ['current', 'd30', 'd60', 'd90'].flatMap(k => ppAging.buckets[k].invoices)
                  : ppAging.buckets[ppAgingOpen].invoices;
                return list.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#999', marginTop: 10 }}>Nothing in this bucket.</div>
                ) : (
                  <table style={{ width: '100%', marginTop: 10, fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead><tr style={{ color: '#999', fontSize: 10, textTransform: 'uppercase' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Invoice</th><th style={{ textAlign: 'left', padding: '4px 6px' }}>Patient</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Amount</th><th style={{ textAlign: 'right', padding: '4px 6px' }}>Sent</th><th style={{ textAlign: 'right', padding: '4px 6px' }}>Days</th>
                    </tr></thead>
                    <tbody>
                      {list.map(i => (
                        <tr key={i.id} style={{ borderTop: '0.5px solid #f0f0f0' }}>
                          <td style={{ padding: '5px 6px', fontWeight: 500 }}>{i.invoice_number}</td>
                          <td style={{ padding: '5px 6px' }}>{i.patient_name || '--'}</td>
                          <td style={{ padding: '5px 6px', textAlign: 'right' }}>{formatCurrency(i.amount)}</td>
                          <td style={{ padding: '5px 6px', textAlign: 'right', color: '#666' }}>{i.sent_at ? format(toET(i.sent_at), 'M/d/yy') : '--'}</td>
                          <td style={{ padding: '5px 6px', textAlign: 'right', color: i.days >= 60 ? '#A32D2D' : '#666' }}>{i.days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}
          {message && (
            <div style={{ background: '#E1F5EE', color: '#0F6E56', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              {message}
            </div>
          )}
          {ppPatients.length === 0 ? (
            <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#999', fontSize: 13 }}>
              No unbilled private pay records.
            </div>
          ) : (
            <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #e0e0db', fontSize: 13, fontWeight: 500 }}>
                Unbilled Patients ({ppPatients.length})
              </div>
              {ppPatients.map((p, i) => {
                const isExpanded = ppExpanded[p.patient_name];
                const recs = ppRecords[p.patient_name] || [];
                return (
                <div key={p.patient_name} style={{ borderBottom: i < ppPatients.length - 1 ? '0.5px solid #f0f0f0' : 'none' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 16px', cursor: 'pointer',
                  }} onClick={async () => {
                    const newExpanded = !isExpanded;
                    setPpExpanded(prev => ({ ...prev, [p.patient_name]: newExpanded }));
                    if (newExpanded && !ppRecords[p.patient_name]) {
                      try {
                        const res = await api.post('/billing/patient-records', { patientName: p.patient_name });
                        setPpRecords(prev => ({ ...prev, [p.patient_name]: res.data.records || [] }));
                      } catch (e) { console.error(e); }
                    }
                  }}>
                    <div style={{ fontSize: 12, color: '#999', width: 16 }}>{isExpanded ? '▼' : '▶'}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{p.patient_name}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>
                        {p.trips} trip{p.trips > 1 ? 's' : ''} — {p.earliest === p.latest ? p.earliest : `${p.earliest} to ${p.latest}`}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{formatCurrency(p.total)}</div>
                    {Number(p.attachment_count) > 0 && (
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: '#EDE9FE', color: '#6D28D9', fontWeight: 500 }}>
                        {p.attachment_count} file{Number(p.attachment_count) > 1 ? 's' : ''}
                      </span>
                    )}
                    {Number(p.drafts) > 0 && (
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: '#FAEEDA', color: '#854F0B', fontWeight: 500 }}>
                        {p.drafts} draft
                      </span>
                    )}
                    {Number(p.approved) > 0 && (
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: '#E1F5EE', color: '#0F6E56', fontWeight: 500 }}>
                        {p.approved} approved
                      </span>
                    )}
                    {Number(p.drafts) > 0 && (
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm(`Approve all records for ${p.patient_name}?`)) return;
                        try {
                          await api.post('/billing/approve-patient', { patientName: p.patient_name });
                          setMessage(`Approved records for ${p.patient_name}`);
                          setTimeout(() => setMessage(''), 3000);
                          const ppRes = await api.get('/billing/private-pay-summary');
                          setPpPatients(ppRes.data.patients || []);
                          // Refresh expanded records
                          const recRes = await api.post('/billing/patient-records', { patientName: p.patient_name });
                          setPpRecords(prev => ({ ...prev, [p.patient_name]: recRes.data.records || [] }));
                        } catch (err) { alert(err.response?.data?.error || 'Failed'); }
                      }} style={{ fontSize: 10, padding: '3px 8px', background: '#FAEEDA', color: '#854F0B', border: '0.5px solid #E6C87A' }}>
                        Approve
                      </button>
                    )}
                    {p.delivery && (
                      <span title={p.delivery.warning || ''} style={{
                        fontSize: 10, padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap',
                        background: p.delivery.method === 'email' ? '#E1F5EE' : (p.delivery.method === 'mail' && !p.delivery.warning ? '#EDF4FB' : '#FCEBEB'),
                        color: p.delivery.method === 'email' ? '#0F6E56' : (p.delivery.method === 'mail' && !p.delivery.warning ? '#185FA5' : '#A32D2D'),
                      }}>
                        {p.delivery.method === 'email' ? 'Email: ' + p.delivery.email
                          : p.delivery.method === 'mail' ? (p.delivery.warning ? 'Mail -- facility room!' : 'Mail')
                          : 'No email or address'}
                        {p.delivery.method !== 'email' && p.delivery.hasFormContact ? ' (facility gave a contact)' : ''}
                      </span>
                    )}
                    {Number(p.drafts) === 0 && Number(p.approved) > 0 && (
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        let nextNums = null;
                        try { nextNums = (await api.get('/billing/next-invoice-numbers')).data; } catch (err) {}
                        // Prefill from the saved address when we have one;
                        // otherwise addressee = patient name, address empty.
                        //
                        // A saved block whose FIRST line is a street (starts
                        // with a house number) has no addressee in it, so
                        // taking line 0 put the street where the name belongs
                        // and the invoice printed with nobody to mail it to
                        // (PP25, caught by Paula 2026-09-21 as Erika was about
                        // to print it). In that case keep the patient name as
                        // the addressee and treat every line as address.
                        let addressee = p.patient_name, address = '';
                        let address2 = '';
                        if (p.billing_address) {
                          const lines = p.billing_address.split('\n').map(s => s.trim()).filter(Boolean);
                          const firstIsStreet = /^\d+\s+\w/.test(lines[0] || '');
                          if (firstIsStreet) {
                            addressee = p.patient_name;
                            if (lines.length >= 3) { address2 = lines[1]; address = lines.slice(2).join(', '); }
                            else address = lines.join(', ');
                          } else {
                            addressee = lines[0] || p.patient_name;
                            // A 4-line block means line 2 is the room/apt line
                            if (lines.length >= 4) { address2 = lines[1]; address = lines.slice(2).join(', '); }
                            else address = lines.slice(1).join(', ');
                          }
                        }
                        setInvModal({ patientName: p.patient_name, next: nextNums, addressee, address, address2, invoiceNumber: '' });
                      }} style={{ fontSize: 10, padding: '3px 8px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>
                        Generate Invoice
                      </button>
                    )}
                  </div>
                  {isExpanded && recs.length > 0 && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, background: '#fafaf8' }}>
                      <thead>
                        <tr style={{ textAlign: 'left' }}>
                          <th style={{ padding: '4px 10px 4px 42px', fontSize: 10, color: '#999' }}>Date</th>
                          <th style={{ padding: '4px 10px', fontSize: 10, color: '#999' }}>Trip #</th>
                          <th style={{ padding: '4px 10px', fontSize: 10, color: '#999' }}>Where To</th>
                          <th style={{ padding: '4px 10px', fontSize: 10, color: '#999', textAlign: 'right' }}>Miles</th>
                          <th style={{ padding: '4px 10px', fontSize: 10, color: '#999' }}>Start</th>
                          <th style={{ padding: '4px 10px', fontSize: 10, color: '#999' }}>Return</th>
                          <th style={{ padding: '4px 10px', fontSize: 10, color: '#999', textAlign: 'right' }}>Hours</th>
                          <th style={{ padding: '4px 10px', fontSize: 10, color: '#999', textAlign: 'right' }}>Total</th>
                          <th style={{ padding: '4px 10px', fontSize: 10, color: '#999' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recs.map(r => {
                          const isEditing = editingId === r.id;
                          return (
                          <tr key={r.id} style={{ borderBottom: '0.5px solid #eee', background: isEditing ? '#FAEEDA22' : '#fafaf8' }}>
                            <td style={{ padding: '5px 10px 5px 42px' }}>
                              {r.vehicle_type === 'wheelchair_van' ? 'W ' : ''}{r.scheduled_time ? format(toET(r.scheduled_time), 'M/d') : '--'}
                              {r.return_leg_of && <span style={{ fontSize: 9, color: '#185FA5', marginLeft: 4 }}>RET</span>}
                              {parseFloat(r.cancellation_fee || 0) > 0 && <span style={{ fontSize: 9, color: '#C62828', marginLeft: 4 }}>CANCEL</span>}
                            </td>
                            <td style={{ padding: '5px 10px', color: '#888' }}>{(r.transport_number || '').replace('TRP-2026-0', '')}</td>
                            {isEditing ? (
                              <td style={{ padding: '3px 6px' }}>
                                <input type="text" value={editForm.destinationAddress || ''} onChange={e => setEditForm(f => ({...f, destinationAddress: e.target.value}))} style={{ width: 130, fontSize: 11, padding: '2px 4px' }} />
                              </td>
                            ) : (
                              <td style={{ padding: '5px 10px', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.billing_description || r.destination_address || '--'}</td>
                            )}
                            {isEditing ? (
                              <td style={{ padding: '3px 6px' }}>
                                <input type="number" step="1" value={editForm.mileageMiles} onChange={e => setEditForm(f => ({...f, mileageMiles: e.target.value}))} style={{ width: 45, fontSize: 11, textAlign: 'right' }} />
                              </td>
                            ) : (
                              <td style={{ padding: '5px 10px', textAlign: 'right' }}>{Number(r.mileage_miles || 0).toFixed(0)}</td>
                            )}
                            {isEditing ? (
                              <td style={{ padding: '3px 6px' }}>
                                <input type="time" value={editForm.startTime || ''} onChange={e => setEditForm(f => ({...f, startTime: e.target.value}))} style={{ width: 75, fontSize: 11 }} />
                              </td>
                            ) : (
                              <td style={{ padding: '5px 10px' }}>{formatTime(r.actual_start_time || r.actual_pickup_time, 'ampm')}</td>
                            )}
                            {isEditing ? (
                              <td style={{ padding: '3px 6px' }}>
                                <input type="time" value={editForm.returnTime || ''} onChange={e => setEditForm(f => ({...f, returnTime: e.target.value}))} style={{ width: 75, fontSize: 11 }} />
                              </td>
                            ) : (
                              <td style={{ padding: '5px 10px' }}>{formatTime(r.actual_return_time || r.actual_dropoff_time, 'ampm')}</td>
                            )}
                            {isEditing ? (
                              <td style={{ padding: '3px 6px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input type="number" step="0.25" value={editForm.billingHours} onChange={e => setEditForm(f => ({...f, billingHours: e.target.value}))} style={{ width: 45, fontSize: 11, textAlign: 'right' }} />
                                  <span style={{ fontSize: 9, color: '#888' }}>x</span>
                                  <input type="number" step="1" min="1" max="4" value={editForm.numTransporters || '1'} onChange={e => setEditForm(f => ({...f, numTransporters: e.target.value}))} style={{ width: 25, fontSize: 11, textAlign: 'center' }} />
                                </div>
                              </td>
                            ) : (
                              <td style={{ padding: '5px 10px', textAlign: 'right' }}>
                                {Number(r.billing_hours || 0).toFixed(2)}
                                {Number(r.num_transporters) > 1 && <span style={{ fontSize: 9, color: '#888', marginLeft: 2 }}>x{r.num_transporters}</span>}
                              </td>
                            )}
                            <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(r.total)}</td>
                            <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>
                              {isEditing ? (
                                <>
                                  <button onClick={() => handleSaveEdit(r.id, p.patient_name)} style={{ fontSize: 10, padding: '2px 6px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5', marginRight: 4 }}>Save</button>
                                  <button onClick={() => setEditingId(null)} style={{ fontSize: 10, padding: '2px 6px', background: '#f5f5f3', color: '#666', border: '0.5px solid #e0e0db' }}>Cancel</button>
                                </>
                              ) : (
                                <>
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingId(r.id);
                                    setEditForm({
                                      billingHours: r.billing_hours || 0,
                                      mileageMiles: r.mileage_miles || 0,
                                      numTransporters: r.num_transporters || 1,
                                      tollAmount: r.toll_amount || 0,
                                      mealAmount: r.meal_amount || 0,
                                      cleanupFee: r.cleanup_fee || 0,
                                      patientName: r.patient_name || '',
                                      patientMrn: r.patient_mrn || '',
                                      startTime: r.actual_start_time ? format(toET(r.actual_start_time), 'HH:mm') : '',
                                      returnTime: r.actual_return_time ? format(toET(r.actual_return_time), 'HH:mm') : '',
                                      destinationAddress: r.billing_description || r.destination_address || '',
                                    });
                                  }} style={{ fontSize: 10, padding: '2px 6px', background: '#f5f5f3', color: '#666', border: '0.5px solid #e0e0db', marginRight: 4 }}>Edit</button>
                                  {r.status === 'draft' && (
                                    <button onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await api.post('/billing/approve-record', { recordId: r.id });
                                        const [recRes, ppRes] = await Promise.all([
                                          api.post('/billing/patient-records', { patientName: p.patient_name }),
                                          api.get('/billing/private-pay-summary'),
                                        ]);
                                        setPpRecords(prev => ({ ...prev, [p.patient_name]: recRes.data.records || [] }));
                                        setPpPatients(ppRes.data.patients || []);
                                      } catch (err) { alert(err.response?.data?.error || 'Failed'); }
                                    }} style={{ fontSize: 10, padding: '2px 6px', background: '#FAEEDA', color: '#854F0B', border: '0.5px solid #E6C87A' }}>Approve</button>
                                  )}
                                  {r.status === 'ready' && (
                                    <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 10, background: '#E1F5EE', color: '#0F6E56' }}>approved</span>
                                  )}
                                  {r.attachments && r.attachments.length > 0 && r.attachments.map(att => (
                                    <a key={att.id} href="#" onClick={(e) => { e.stopPropagation(); openFile(att.id, e); }}
                                      title={att.original_filename}
                                      style={{ fontSize: 10, padding: '2px 6px', background: '#EDE9FE', color: '#6D28D9', border: '0.5px solid #C4B5FD', borderRadius: 4, marginLeft: 4, textDecoration: 'none', cursor: 'pointer' }}>
                                      {att.doc_type === 'fax' ? 'Fax' : att.doc_type === 'facility_attachment' ? 'Doc' : att.doc_type === 'gas_receipt' ? 'Receipt' : 'File'}
                                    </a>
                                  ))}
                                </>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {isExpanded && recs.length === 0 && (
                    <div style={{ padding: '8px 42px', fontSize: 11, color: '#999' }}>Loading...</div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      ) : loading ? (
        <div style={{ color: '#999', fontSize: 13, textAlign: 'center', padding: '2rem' }}>Loading...</div>
      ) : records.length === 0 ? (
        <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#999', fontSize: 13 }}>
          No completed trips for {facility} this week.
        </div>
      ) : (
        <>
          {/* Invoice-style trip table */}
          <div id="billing-print-area" style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'auto' }}>
            <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e0e0db' }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>Commonwealth Transport LLC</div>
              <div style={{ fontSize: 11, color: '#666' }}>554 W King St, Ste 101, Lancaster, PA 17603</div>
              <div style={{ fontSize: 12, fontWeight: 500, marginTop: 8 }}>
                {facility} — {format(weekStart, 'M/d')}–{format(weekEnd, 'M/d/yyyy')}
                {nextInvoiceNumber && (
                  <span style={{ marginLeft: 12, color: '#185FA5' }}>Invoice #{nextInvoiceNumber}</span>
                )}
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#f5f5f3', textAlign: 'left' }}>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db' }}>Date</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db' }}>Trip #</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db' }}>Patient</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db' }}>Where To</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db', textAlign: 'right' }}>Miles</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db' }}>Start</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db' }}>Return</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db', textAlign: 'right' }}>Hours</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db', textAlign: 'right' }}>Tolls</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db', textAlign: 'right' }}>Meals</th>
                  <th style={{ padding: '8px 10px', borderBottom: '0.5px solid #e0e0db', textAlign: 'right', fontWeight: 600 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, idx) => {
                  const pt = extractPatientInfo(r.notes);
                  const tripDate = r.scheduled_time ? format(toET(r.scheduled_time), 'M/d') : '--';
                  const isEditing = editingId === r.id;
                  const isInvoiced = !!r.invoice_id;
                  const isReturnLeg = !!r.return_leg_of;
                  const tripNum = r.transport_number ? r.transport_number.replace('TRP-2026-0', '') : '';
                  const hasReturn = records.some(other => other.outbound_transport_number === r.transport_number);
                  return (
                    <tr key={r.id} style={{
                      borderBottom: (isReturnLeg || hasReturn) && !isReturnLeg ? 'none' : '0.5px solid #f0f0f0',
                      background: isEditing ? '#FAEEDA22' : isReturnLeg ? '#f9f9f7' : 'white',
                    }}>
                      <td style={{ padding: '6px 10px' }}>
                        {r.vehicle_type === 'wheelchair_van' ? 'W ' : ''}{tripDate}
                        {isReturnLeg && <div style={{ fontSize: 9, color: '#185FA5', fontWeight: 500 }}>RETURN</div>}
                        {parseFloat(r.cancellation_fee || 0) > 0 && <div style={{ fontSize: 9, color: '#C62828', fontWeight: 600 }}>CANCEL</div>}
                      </td>
                      <td style={{ padding: '6px 10px', fontSize: 10, color: '#888' }}>
                        {r.transport_number ? r.transport_number.replace('TRP-2026-0', '') : '--'}
                        {r.fax_file_id ? (
                          <a href="#" onClick={(e) => openFile(r.fax_file_id, e)}
                            style={{ marginLeft: 4, fontSize: 9, color: '#185FA5', textDecoration: 'none' }}
                            title="View fax PDF">FAX</a>
                        ) : (
                          <label title="Attach fax PDF" style={{ marginLeft: 4, fontSize: 9, color: '#999', cursor: 'pointer', textDecoration: 'underline' }}>
                            +fax
                            <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('category', 'fax');
                                formData.append('docType', 'fax');
                                formData.append('transportId', r.transport_id);
                                await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                                // Refresh records to show the new FAX link
                                const res2 = await api.get(`/billing/records?facility=${facility}&startDate=${startDate}&endDate=${endDate}`);
                                setRecords(res2.data.records || []);
                              } catch (err) {
                                alert('Upload failed: ' + (err.response?.data?.error || err.message));
                              }
                            }} />
                          </label>
                        )}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        {isEditing ? (
                          <>
                            <input type="text" value={editForm.patientName || ''} placeholder="Patient name"
                              onChange={e => setEditForm(f => ({...f, patientName: e.target.value}))}
                              style={{ width: '100%', fontSize: 11, padding: '2px 4px', marginBottom: 2 }} />
                            <input type="text" value={editForm.patientMrn || ''}
                              placeholder={patientIdLabel(r.billing_facility || facility) || 'MRN / DOB'}
                              onChange={e => setEditForm(f => ({...f, patientMrn: e.target.value}))}
                              style={{ width: '100%', fontSize: 10, padding: '2px 4px', color: '#666' }} />
                          </>
                        ) : (
                          <>
                            {r.patient_name || r.patient_name_display || pt.name || '--'}
                            {(r.patient_mrn || pt.id) && (
                              <div style={{ fontSize: 10, color: '#999' }}>
                                {(pt.idKind && !r.patient_mrn ? pt.idKind + ' ' : '')}{r.patient_mrn || pt.id}
                              </div>
                            )}
                            {/* Only flag a MISSING identifier for a facility that actually issues one.
                                LBHH uses DOB, not MRN; other payers provide neither. */}
                            {!r.patient_mrn && !pt.id && patientIdLabel(r.billing_facility || facility) && (
                              <div style={{ fontSize: 10, color: '#C62828' }}>
                                No {patientIdLabel(r.billing_facility || facility)}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      {isEditing ? (
                        <td style={{ padding: '4px 6px' }}>
                          <input type="text" value={editForm.destinationAddress || ''} placeholder="Destination"
                            onChange={e => setEditForm(f => ({...f, destinationAddress: e.target.value}))}
                            style={{ width: 130, fontSize: 11, padding: '2px 4px' }} />
                        </td>
                      ) : (
                        <td style={{ padding: '6px 10px', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {facility === 'LGH' ? (r.destination_address || '--') : (r.billing_description || r.destination_address || '--')}
                        </td>
                      )}
                      {isEditing ? (
                        <td style={{ padding: '4px 6px' }}>
                          <input type="number" step="1" value={editForm.mileageMiles}
                            onChange={e => setEditForm(f => ({...f, mileageMiles: e.target.value}))}
                            style={{ width: 50, fontSize: 11, textAlign: 'right' }} />
                        </td>
                      ) : (
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{Number(r.mileage_miles || 0).toFixed(0)}</td>
                      )}
                      {isEditing ? (
                        <td style={{ padding: '4px 6px' }}>
                          <input type="time" value={editForm.startTime || ''} onChange={e => setEditForm(f => ({...f, startTime: e.target.value}))} style={{ width: 75, fontSize: 11 }} />
                        </td>
                      ) : (
                        <td style={{ padding: '6px 10px' }}>{formatTime(r.actual_start_time || r.actual_pickup_time, 'ampm')}</td>
                      )}
                      {isEditing ? (
                        <td style={{ padding: '4px 6px' }}>
                          <input type="time" value={editForm.returnTime || ''} onChange={e => setEditForm(f => ({...f, returnTime: e.target.value}))} style={{ width: 75, fontSize: 11 }} />
                        </td>
                      ) : (
                        <td style={{ padding: '6px 10px' }}>{formatTime(r.actual_return_time || r.actual_dropoff_time, 'ampm')}</td>
                      )}
                      {isEditing ? (
                        <td style={{ padding: '4px 6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input type="number" step="0.25" value={editForm.billingHours}
                              onChange={e => setEditForm(f => ({...f, billingHours: e.target.value}))}
                              style={{ width: 45, fontSize: 11, textAlign: 'right' }} />
                            <span style={{ fontSize: 9, color: '#888' }}>x</span>
                            <input type="number" step="1" min="1" max="4" value={editForm.numTransporters || '1'}
                              onChange={e => setEditForm(f => ({...f, numTransporters: e.target.value}))}
                              style={{ width: 25, fontSize: 11, textAlign: 'center' }} title="Transporters" />
                          </div>
                        </td>
                      ) : (
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                          {Number(r.billing_hours || 0).toFixed(2)}
                          {Number(r.num_transporters) > 1 && <span style={{ fontSize: 9, color: '#888', marginLeft: 2 }}>x{r.num_transporters}</span>}
                        </td>
                      )}
                      {isEditing ? (
                        <>
                          <td style={{ padding: '4px 6px' }}>
                            <input type="number" step="0.01" value={editForm.tollAmount}
                              onChange={e => setEditForm(f => ({...f, tollAmount: e.target.value}))}
                              style={{ width: 60, fontSize: 11, textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: '4px 6px' }}>
                            <input type="number" step="0.01" value={editForm.mealAmount}
                              onChange={e => setEditForm(f => ({...f, mealAmount: e.target.value}))}
                              style={{ width: 60, fontSize: 11, textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                            <button onClick={() => handleSaveEdit(r.id)}
                              style={{ fontSize: 10, padding: '2px 8px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: 4 }}>Save</button>
                            <button onClick={() => setEditingId(null)}
                              style={{ fontSize: 10, padding: '2px 6px', marginLeft: 4 }}>X</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                            {Number(r.toll_amount) > 0
                              ? formatCurrency(r.toll_amount)
                              : r.toll_flagged_by_driver
                                ? <span style={{ color: '#D32F2F', fontWeight: 600, fontSize: 10 }}>NEEDS TOLL</span>
                                : '0'}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>{Number(r.meal_amount) > 0 ? formatCurrency(r.meal_amount) : '0'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 500 }}>
                            {formatCurrency(r.total)}
                            {!isInvoiced && (
                              <button onClick={() => {
                                setEditingId(r.id);
                                const toTimeStr = (ts) => { if (!ts) return ''; try { const d = toET(ts); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; } catch(e) { return ''; } };
                                setEditForm({ billingHours: r.billing_hours || '0', mileageMiles: r.mileage_miles || '0', tollAmount: r.toll_amount || '0', mealAmount: r.meal_amount || '0', cleanupFee: r.cleanup_fee || '0', numTransporters: r.num_transporters || '1', patientName: r.patient_name || pt.name || '', patientMrn: r.patient_mrn || pt.id || '', startTime: toTimeStr(r.actual_start_time || r.actual_pickup_time), returnTime: toTimeStr(r.actual_return_time || r.actual_dropoff_time), destinationAddress: r.destination_address || '' });
                              }} style={{ fontSize: 9, padding: '1px 5px', marginLeft: 6, color: '#185FA5', background: '#E6F1FB', border: '0.5px solid #B5D4F4' }}>
                                Edit
                              </button>
                            )}
                            {facility === 'Private Pay' && !r.qb_invoice_id && (
                              <button onClick={async () => {
                                const email = prompt('Patient email (optional — for QB payment link):');
                                try {
                                  const res = await api.post('/quickbooks/create-patient-invoice', { transportId: r.transport_id, patientEmail: email || undefined });
                                  setMessage(`Patient invoice created — QB #${res.data.docNumber || res.data.qbInvoiceId} — $${Number(res.data.total).toFixed(2)}`);
                                  window.location.reload();
                                } catch (err) { alert(err.response?.data?.error || 'Failed to create invoice'); }
                              }} style={{ fontSize: 9, padding: '1px 5px', marginLeft: 4, color: '#0F6E56', background: '#E1F5EE', border: '0.5px solid #5DCAA5' }}>
                                Invoice → QB
                              </button>
                            )}
                            {facility === 'Private Pay' && r.qb_invoice_id && (
                              <>
                                <span style={{ fontSize: 9, color: '#0F6E56', marginLeft: 4 }}>QB ✓</span>
                                <button onClick={async () => {
                                  const method = window.confirm('Send via EMAIL? (Cancel for TEXT)') ? 'email' : 'sms';
                                  const contact = prompt(method === 'email' ? 'Patient email:' : 'Patient phone (10 digits):');
                                  if (!contact) return;
                                  try {
                                    await api.post('/quickbooks/send-patient-invoice', { transportId: r.transport_id, method, [method === 'email' ? 'email' : 'phone']: contact });
                                    setMessage('Invoice sent to patient!');
                                  } catch (err) { alert(err.response?.data?.error || 'Send failed'); }
                                }} style={{ fontSize: 9, padding: '1px 5px', marginLeft: 4, color: '#185FA5', background: '#E6F1FB', border: '0.5px solid #B5D4F4' }}>
                                  Send to patient
                                </button>
                              </>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #185FA5', fontWeight: 600 }}>
                  <td colSpan={3} style={{ padding: '8px 10px' }}>Totals</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(totals.miles || 0).toFixed(0)}</td>
                  <td colSpan={2}></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(totals.hours || 0).toFixed(2)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(totals.tolls) > 0 ? formatCurrency(totals.tolls) : '0'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Number(totals.meals) > 0 ? formatCurrency(totals.meals) : '0'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: '#185FA5', fontSize: 13 }}>{formatCurrency(totals.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Actions */}
          {(() => {
            const pendingReview = records.filter(r => r.status === 'draft' && !r.invoice_id);
            const approved = records.filter(r => r.status === 'ready' && !r.invoice_id);
            const allApproved = pendingReview.length === 0 && approved.length > 0;
            return (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                {pendingReview.length > 0 && (
                  <button onClick={handleApproveWeek} disabled={approving}
                    style={{ fontSize: 13, padding: '8px 16px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, fontWeight: 500 }}>
                    {approving ? 'Approving...' : `Approve week (${pendingReview.length} records)`}
                  </button>
                )}
                {allApproved && (
                  <button onClick={handleGenerateInvoice} disabled={generating}
                    style={{ fontSize: 13, padding: '8px 16px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: 8, fontWeight: 500 }}>
                    {generating ? 'Generating...' : `Generate invoice (${approved.length} trips)`}
                  </button>
                )}
                {pendingReview.length > 0 && !allApproved && (
                  <div style={{ fontSize: 11, color: '#854F0B', alignSelf: 'center' }}>
                    Review and approve before generating invoice
                  </div>
                )}
                <button onClick={() => window.print()}
                  style={{ fontSize: 13, padding: '8px 16px', background: '#f0f0f0', border: '0.5px solid #e0e0db', borderRadius: 8 }}>
                  Print
                </button>
              </div>
            );
          })()}
        </>
      )}

      {/* Recent invoices */}
      {invoices.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Recent {facility} invoices</div>
            <input value={invoiceSearch} onChange={e => setInvoiceSearch(e.target.value)}
              placeholder="Find invoice # or name..."
              style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #ddd', borderRadius: 6, width: 180 }} />
          </div>
          <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'hidden' }}>
            {invoices.filter(inv => {
              if (!invoiceSearch.trim()) return true;
              const q = invoiceSearch.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
              return (inv.invoice_number || '').toLowerCase().replace(/[^a-z0-9]/g, '').includes(q)
                || (inv.billing_address || '').toLowerCase().includes(invoiceSearch.trim().toLowerCase());
            }).slice(0, 25).map((inv, i) => {
              const statusColor = inv.status === 'paid' ? '#0F6E56' : inv.status === 'sent' ? '#185FA5' : '#854F0B';
              const periodStart = inv.period_start instanceof Date ? inv.period_start : new Date(inv.period_start);
              const periodEnd = inv.period_end instanceof Date ? inv.period_end : new Date(inv.period_end);
              return (
                <div key={inv.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px',
                  borderBottom: i < 9 ? '0.5px solid #f0f0f0' : 'none',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Invoice #{inv.invoice_number}</div>
                    <div style={{ fontSize: 11, color: '#999' }}>
                      {format(periodStart, 'M/d')}–{format(periodEnd, 'M/d/yyyy')}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{formatCurrency(inv.grand_total)}</div>
                  <span style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 500,
                    background: inv.status === 'paid' ? '#E1F5EE' : inv.status === 'sent' ? '#E6F1FB' : '#FAEEDA',
                    color: statusColor,
                  }}>
                    {inv.status}
                  </span>
                  {facility === 'Private Pay' && inv.autopay_charge_at && inv.status === 'sent' && !inv.autopay_attempted_at && (
                    <>
                      <span style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 600,
                        background: inv.autopay_hold ? '#FCEBEB' : '#EDF4FB',
                        color: inv.autopay_hold ? '#A32D2D' : '#185FA5',
                      }}>
                        {inv.autopay_hold ? 'Auto-pay HELD' : 'Auto-pay ' + format(toET(inv.autopay_charge_at), 'M/d h a')}
                      </span>
                      <button onClick={async () => {
                        try {
                          var hr = await api.post('/billing/invoices/' + inv.id + '/autopay-hold', { hold: !inv.autopay_hold });
                          if (hr.data.warning) alert(hr.data.warning);
                          window.location.reload();
                        } catch (err) { alert(err.response?.data?.error || 'Failed'); }
                      }} style={{
                        fontSize: 10, padding: '3px 8px',
                        background: inv.autopay_hold ? '#E1F5EE' : '#FCEBEB',
                        color: inv.autopay_hold ? '#0F6E56' : '#A32D2D',
                        border: '0.5px solid ' + (inv.autopay_hold ? '#5DCAA5' : '#F09595'),
                      }}>
                        {inv.autopay_hold ? 'Release' : 'Hold'}
                      </button>
                    </>
                  )}
                  {facility === 'Private Pay' && inv.delivery_method && inv.status !== 'paid' && (
                    <span title={inv.delivery_warning || ''} style={{
                      fontSize: 10, padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap',
                      background: inv.delivery_method === 'email' ? '#E1F5EE' : (inv.delivery_method === 'mail' && !inv.delivery_warning ? '#EDF4FB' : '#FCEBEB'),
                      color: inv.delivery_method === 'email' ? '#0F6E56' : (inv.delivery_method === 'mail' && !inv.delivery_warning ? '#185FA5' : '#A32D2D'),
                    }}>
                      {inv.delivery_method === 'email' ? 'Goes by email'
                        : inv.delivery_method === 'mail' ? (inv.delivery_warning ? 'Mail -- facility room!' : 'Goes by mail')
                        : 'No email or address'}
                    </span>
                  )}
                  {facility === 'Private Pay' && (
                    <button onClick={() => {
                      // Split the stored address block back into the modal's fields:
                      // line 1 = addressee; 4+ lines = room line + street + city/state/zip;
                      // 3 lines = street + city/state/zip; 2 lines = single address line.
                      const lines = (inv.billing_address || '').split('\n').map(s => s.trim()).filter(Boolean);
                      // Same street-as-addressee trap as the generate prefill.
                      const firstIsStreet = /^\d+\s+\w/.test(lines[0] || '');
                      const addressee = firstIsStreet ? '' : (lines[0] || '');
                      let address2 = '', address = '';
                      if (firstIsStreet) {
                        if (lines.length >= 3) { address2 = lines[1]; address = lines.slice(2).join(', '); }
                        else address = lines.join(', ');
                      }
                      else if (lines.length >= 4) { address2 = lines[1]; address = lines.slice(2).join(', '); }
                      else if (lines.length === 3) { address = lines.slice(1).join(', '); }
                      else if (lines.length === 2) { address = lines[1]; }
                      setInvModal({ mode: 'edit', invoiceId: inv.id, invoiceNumber: inv.invoice_number, patientName: addressee, addressee, address, address2 });
                    }} style={{ fontSize: 10, padding: '3px 8px', background: '#FFF7E6', color: '#854F0B', border: '0.5px solid #FAC775' }}>
                      Edit address
                    </button>
                  )}
                  <button onClick={() => printInvoiceById(inv.id, facility, 'view')}
                    style={{ fontSize: 10, padding: '3px 8px', background: '#E6F1FB', color: '#185FA5', border: '0.5px solid #90BEE8' }}>
                    View
                  </button>
                  <button onClick={() => printInvoiceById(inv.id, facility, 'print', inv.status, () => window.location.reload())}
                    style={{ fontSize: 10, padding: '3px 8px', background: '#f7f7f5', color: '#333', border: '0.5px solid #e0e0db' }}>
                    Print
                  </button>
                  {inv.status === 'draft' && (
                    <button onClick={async () => {
                      if (!window.confirm('Mark invoice #' + inv.invoice_number + ' as sent? (use this when it went out by hand)')) return;
                      try {
                        await api.put('/billing/invoices/' + inv.id, { status: 'sent' });
                        window.location.reload();
                      } catch (err) { alert(err.response?.data?.error || 'Failed to update invoice'); }
                    }} style={{ fontSize: 10, padding: '3px 8px', background: '#F3EEFB', color: '#5B3FA8', border: '0.5px solid #B9A3E3' }}>
                      Mark sent
                    </button>
                  )}
                  {(inv.status === 'draft' || inv.status === 'sent') && (
                    <button onClick={async () => {
                      var isPrivate = facility === 'Private Pay';
                      var body = {};
                      if (isPrivate && inv.email_optin) {
                        if (!window.confirm('Email invoice #' + inv.invoice_number + ' to the customer (CC you)?')) return;
                      } else if (isPrivate) {
                        // No email on file: offer typing one, else print & mail
                        var typed = window.prompt('Customer email for invoice #' + inv.invoice_number + '?\n\nEnter an email to send it to the customer (saved for next time), or leave empty to send to you for print & mail.', '');
                        if (typed === null) return;
                        typed = typed.trim();
                        if (typed) body.email = typed;
                        else if (!window.confirm('Send invoice #' + inv.invoice_number + ' to paula@commonwealthtransport.com for printing?')) return;
                      } else {
                        if (!window.confirm('Email invoice #' + inv.invoice_number + ' to ' + facility + ' contacts?')) return;
                      }
                      try {
                        var r = await api.post('/billing/send-invoice/' + inv.id, body);
                        alert('Invoice sent! Sent to ' + r.data.sentTo + (r.data.cc ? ' (CC: ' + r.data.cc + ')' : ''));
                        window.location.reload();
                      } catch (err) { alert(err.response?.data?.error || 'Failed to send invoice'); }
                    }} style={{ fontSize: 10, padding: '3px 8px', background: '#FFF3E0', color: '#E65100', border: '0.5px solid #FFB74D' }}>
                      {facility === 'Private Pay' ? (inv.email_optin ? 'Email Customer' : 'Send / Email') : 'Email Invoice'}
                    </button>
                  )}
                  {inv.status !== 'paid' && (
                    <button onClick={() => handleMarkPaid(inv.id)}
                      style={{ fontSize: 10, padding: '3px 8px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>
                      Mark paid
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
